import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { BsPatchCheckFill } from "react-icons/bs";
import { FaStar } from "react-icons/fa6";
import { filterVisibleVendorServices } from "../../utils/ServiceTypeMapping";
import config from "../../config";

const DAYS_OF_WEEK = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const formatTimeForDisplay = (time24h) => {
  if (!time24h) return "";
  const [h, m] = time24h.split(":").map(Number);

  if (isNaN(h) || isNaN(m)) return "N/A";

  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period.toLowerCase()}`;
};

const isTimeWithinSlot = (start24h, end24h, currentTime24h) => {
  const timeToMinutes = (timeStr) => {
    const [h, m] = timeStr.split(":").map(Number);
    return h * 60 + m;
  };

  const currentMinutes = timeToMinutes(currentTime24h);
  const startMinutes = timeToMinutes(start24h);
  const endMinutes = timeToMinutes(end24h);

  return currentMinutes >= startMinutes && currentMinutes < endMinutes;
};

const getNextOpenTime = (businessHours, currentDayIndex, currentTime24h) => {
  const isValidSchedule = (schedule) =>
    schedule &&
    schedule.day &&
    !schedule.closed &&
    Array.isArray(schedule.slots) &&
    schedule.slots.length > 0;

  const todaySchedule = businessHours?.find(
    (day) => day.day === DAYS_OF_WEEK[currentDayIndex]
  );

  if (isValidSchedule(todaySchedule)) {
    for (const slot of todaySchedule.slots) {
      if (slot.start > currentTime24h) {
        const startTime = formatTimeForDisplay(slot.start);
        return { day: "Today", time: startTime };
      }
    }
  }

  for (let i = 1; i <= 7; i++) {
    const nextDayIndex = (currentDayIndex + i) % 7;
    const nextDayName = DAYS_OF_WEEK[nextDayIndex];

    const schedule = businessHours?.find((day) => day.day === nextDayName);

    if (isValidSchedule(schedule)) {
      const startTime = formatTimeForDisplay(schedule.slots[0].start);
      const displayDay = i === 1 ? "Tomorrow" : nextDayName;
      return { day: displayDay, time: startTime };
    }
  }
  return null;
};

const getTodayBusinessInfo = (businessHours) => {
  if (!Array.isArray(businessHours) || businessHours.length === 0) {
    return { status: "Closed", timeDisplay: "Closed indefinitely" };
  }

  const now = new Date();
  const currentDayIndex = now.getDay();
  const currentTotalMinutes = now.getHours() * 60 + now.getMinutes();
  const currentTime24h = `${String(now.getHours()).padStart(2, "0")}:${String(
    now.getMinutes()
  ).padStart(2, "0")}`;
  const todaySchedule = businessHours?.find(
    (day) => day.day === DAYS_OF_WEEK[currentDayIndex]
  );

  if (
    todaySchedule &&
    !todaySchedule.closed &&
    Array.isArray(todaySchedule.slots) &&
    todaySchedule.slots.length > 0
  ) {
    let isOpen = false;
    let displaySlot = todaySchedule.slots[0];

    for (const slot of todaySchedule.slots) {
      if (isTimeWithinSlot(slot.start, slot.end, currentTime24h)) {
        const [endHour, endMinute] = slot.end.split(":").map(Number);
        const endTotalMinutes = endHour * 60 + endMinute;
        const minutesRemaining = endTotalMinutes - currentTotalMinutes;

        const endDisplay = formatTimeForDisplay(slot.end);
        const timeOnly = endDisplay;

        if (minutesRemaining <= 30) {
          return {
            status: "Closing Soon",
            timeDisplay: timeOnly,
          };
        }

        return {
          status: "Open",
          timeDisplay: timeOnly,
        };

        isOpen = true;
        displaySlot = slot;
        break;
      }
    }
  }

  const nextOpen = getNextOpenTime(
    businessHours,
    currentDayIndex,
    currentTime24h
  );

  if (nextOpen) {
    const openText = `Opens ${nextOpen.day} at ${nextOpen.time}`;

    return {
      status: "Closed",
      timeDisplay: openText,
    };
  }

  return { status: "Closed", timeDisplay: "Closed indefinitely" };
};

const FilterCard = ({ vendor = {} }) => {
 
  const [favorites, setFavorites] = useState([]);
  const [showHours, setShowHours] = useState(false);
  const navigate = useNavigate();
  const visibleServices = filterVisibleVendorServices(vendor.services);

  const [googleRatingData, setGoogleRatingData] = useState({
    rating: Number(vendor.google_rating) || 0,
    reviews_count: Number(vendor.google_review_count) || 0,
  });

  const [fetchedRatingData, setFetchedRatingData] = useState({
    rating: null,
    reviews_count: null,
  });

  const businessInfo = useMemo(() => {
    const hours = Array.isArray(vendor.businessHours)
      ? vendor.businessHours
      : [];

    return getTodayBusinessInfo(hours);
  }, [vendor.businessHours, vendor.brand, vendor.id]);

  useEffect(() => {
    const vendorId = vendor._id;
    if (!vendorId) return;


    const fetchFlipsRating = async () => {
      try {
        const response = await fetch(
          `${config.API_BASE_URL}/customer/getRating/${vendorId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch rating data");
        }

        const data = await response.json();

        if (data.status && data.data && data.data.length > 0) {
          const { average_rating, reviews_count } = data.data[0];
          setFetchedRatingData({
            rating: average_rating,
            reviews_count: reviews_count,
          });
        } else {
          setFetchedRatingData({ rating: 0, reviews_count: 0 });
        }
      } catch (error) {
        console.error("Error fetching vendor rating:", error);
        setFetchedRatingData({ rating: 0, reviews_count: 0 });
      }
    };

    fetchFlipsRating();
   // fetchGoogleRating();
  }, [vendor._id, vendor.google_rating, vendor.google_review_count]);

  const finalGoogleRating = googleRatingData.rating;
  const finalGoogleReviewCount = googleRatingData.reviews_count;

  const displayRating =
    fetchedRatingData.rating !== null
      ? fetchedRatingData.rating
      : Number(vendor.rating) || 0;

  const displayReviewsCount =
    fetchedRatingData.reviews_count !== null
      ? fetchedRatingData.reviews_count
      : Number(vendor.reviews_count) || 0;

  const toggleFavorite = (id) => {
    if (!id) return;
    setFavorites((prev) =>
      prev.includes(id) ? prev.filter((favId) => favId !== id) : [...prev, id]
    );
  };

  const handleCardClick = async () => {
    if (!vendor._id) return;

    try {
      const tokenResponse = await fetch(
        `${config.API_BASE_URL}/customer/shareVendorProfile`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ vendor_id: vendor._id }),
        }
      );

      const tokenData = await tokenResponse.json();

      if (!tokenResponse.ok || !tokenData?.data?.token) {
        throw new Error(tokenData.message || "Token generation failed");
      }

      const token = tokenData.data.token;
      const name = vendor.name?.replace(/\s+/g, "-").toLowerCase();
      const company = vendor.brand?.replace(/\s+/g, "-").toLowerCase();
      const companyName = vendor.companyName;

      localStorage.setItem("vendor_name", vendor.name || "");
      localStorage.setItem("vendor_brand", vendor.brand || "");
      localStorage.setItem("vendor_companyName", vendor.companyName || "");

      navigate(`/${token}/${name}-${company}`);
    } catch (error) {
      console.error("Error during vendor navigation:", error.message);
    }
  };

  const location = useLocation();

  useEffect(() => {
    localStorage.removeItem("vendor_name");
    localStorage.removeItem("vendor_brand");
    localStorage.removeItem("vendor_companyName");
  }, [location.pathname]);

  const formatTo12Hour = (timeStr) => {
    if (!timeStr) return "";
    const [hour, minute] = timeStr.split(":").map(Number);
    const date = new Date();
    date.setHours(hour);
    date.setMinutes(minute);
    return date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  

  const renderFullBusinessHours = () => {
    if (
      !Array.isArray(vendor.businessHours) ||
      vendor.businessHours.length === 0
    ) {
      return (
        <p className="text-sm text-gray-500">No detailed hours available.</p>
      );
    }

    const now = new Date();
    const currentDayIndex = now.getDay();

    const orderedHours = [];
    for (let i = 0; i < 7; i++) {
      const targetDayIndex = (currentDayIndex + i) % 7;
      const dayName = DAYS_OF_WEEK[targetDayIndex];

      const daySchedule = vendor.businessHours.find(
        (schedule) => schedule.day === dayName
      );

      orderedHours.push(
        daySchedule || { day: dayName, closed: true, slots: [] }
      );
    }

    return (
      <React.Fragment>
        {orderedHours.map((daySchedule, index) => {
          const dayName = daySchedule.day;

          let hoursDisplay;
          if (
            daySchedule.closed ||
            !daySchedule.slots ||
            daySchedule.slots.length === 0
          ) {
            hoursDisplay = (
              <span className="text-red-500 font-medium whitespace-nowrap">
                Closed
              </span>
            );
          } else {
            const slotsText = daySchedule.slots
              .map(
                (slot) =>
                  `${formatTimeForDisplay(slot.start)} - ${formatTimeForDisplay(
                    slot.end
                  )}`
              )
              .join(", ");
            hoursDisplay = (
              <span className="text-gray-700 whitespace-nowrap">
                {slotsText}
              </span>
            );
          }

          return (
            <div
              key={dayName}
              className={`flex justify-between text-xs py-0.5 border-b last:border-b-0 border-gray-100 ${
                index === 0
                  ? "font-semibold bg-gray-50 rounded-md -mx-1 px-1"
                  : ""
              }`}
            >
              <span className="font-semibold w-20 flex-shrink-0">
                {dayName}
              </span>
              {hoursDisplay}
            </div>
          );
        })}
      </React.Fragment>
    );
  };

  //const fullAddress = [vendor.full_address, vendor.locality].filter(Boolean).join(", ");

  //const shortAddress = [vendor.locality].filter(Boolean).join(", ");
  //const finalDisplayAddress = shortAddress || fullAddress;

  const getDisplayAddress = () => {
    // 1. Check if locality exists and is valid
    const hasLocality = vendor.locality && 
                        vendor.locality !== "N/A" && 
                        vendor.locality.trim() !== "";
    
    if (hasLocality) {
      return vendor.locality;
    }

    // 2. Fallback to Area (which contains full_address from your flatVendor mapping)
    const hasArea = vendor.area && 
                    vendor.area !== "N/A" && 
                    vendor.area.trim() !== "";

    if (hasArea) {
      return vendor.area;
    }

    // 3. Optional: Final fallback to City if everything else is empty
    return vendor.city !== "N/A" ? vendor.city : null;
  };

  const finalDisplayAddress = getDisplayAddress();

  const isShopOpen = businessInfo.status === "Open";
  const availabilityText = isShopOpen
    ? vendor.available || "Availability unknown"
    : "Unavailable";

  const availabilityClass =
    isShopOpen && !vendor.available?.includes("Not")
      ? "text-[#39C408] font-medium"
      : "text-red-500 font-medium";

      const minPrice = (() => {
          try {
            // 1. Priority: Offline Price Settings
            if (vendor.priceSettings?.minimumPrice) {
              return vendor.priceSettings.minimumPrice;
            }

            // 2. Fallback: Calculate from basic_price of nested services
            //const allPrices = vendor.services
            //  ?.flatMap((s) => s.services || [])
             // .map((s) => s.basic_price)
            //  .filter((p) => typeof p === "number" && p > 0);
          //  return allPrices?.length > 0 ? Math.min(...allPrices) : null;
          } catch {
            return null;
          }
        })();

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div
        className="my-4 rounded-2xl shadow-lg hover:shadow-xl border border-gray-200 bg-white p-4 sm:p-6 lg:p-8 relative cursor-pointer transition-all duration-300 overflow-hidden group"
        onClick={handleCardClick}
      >
        {/* Subtle gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/0 to-red-50/0 group-hover:from-orange-50/30 group-hover:to-red-50/30 transition-all duration-500 pointer-events-none rounded-2xl"></div>

        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 lg:gap-8 w-full relative">
          {/* LEFT SECTION - Profile & Badge */}
          <div className="flex flex-col sm:flex-col items-center gap-4 sm:gap-6 w-full sm:w-auto sm:min-w-0">
            {/* Service Type Badge */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl flex items-center gap-2 shadow-sm border border-orange-100 whitespace-nowrap">
              <img
                src="img/home.png"
                alt="Type"
                className="w-4 h-4 sm:w-5 lg:w-6"
              />
              <p className="text-orange-600 font-semibold text-xs sm:text-sm tracking-wide">
                {vendor.subscription_type?.toUpperCase() === "HOME"
                  ? "Home Service"
                  : vendor.subscription_type?.toUpperCase() === "SHOP"
                  ? "In-Store"
                  : ""}
              </p>
            </div>

            {/* Profile Image */}
            <div className="relative flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-red-500 rounded-full blur-md opacity-20 group-hover:opacity-40 transition-opacity duration-300"></div>
              <img
                  src={
                    vendor?.photo
                      ? `${config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL}/${vendor.photo}`
                      : vendor?.logo
                      ? vendor.logo
                      : "/img/dogLogo.png"
                  }
                  alt={vendor.name || "Profile"}
                  className="relative w-24 h-24 sm:w-28 sm:h-28 lg:w-36 lg:h-36 rounded-full object-cover border-4 border-white shadow-lg ring-2 ring-gray-100"
                  onError={(e) => {
                    e.target.src = "/img/dogLogo.png";
                  }}
                />
              <BsPatchCheckFill className="absolute bottom-0 right-0 w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-white bg-blue-600 rounded-full p-1 border-2 border-white shadow-md" />
            </div>
          </div>

          {/* MIDDLE + RIGHT SECTION - Stacked on mobile, side-by-side on sm+ */}
          <div className="flex-1 w-full flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 lg:gap-8">
            {/* MIDDLE SECTION - Details */}
            <div className="flex-1">
              {/* Name */}
              <h3 className="text-gray-900 text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight mb-2">
                {vendor.brand||vendor.companyName}
              </h3>

              {/* Rating */}
              <div className="flex flex-wrap items-center gap-3 mb-3">
                {finalGoogleRating > 0 && finalGoogleReviewCount > 0 ? (
                  <div className="flex items-center gap-2">
                    <FaStar className="w-4 h-4 sm:w-5 text-yellow-400" />
                    <span className="text-blue-700 font-bold text-base sm:text-lg">
                      {finalGoogleRating.toFixed(1)}
                    </span>
                    <span className="text-gray-600 text-sm">
                      ({finalGoogleReviewCount.toLocaleString()} reviews)
                    </span>
                  </div>
                ) : displayRating > 0 ? (
                  <div className="flex items-center gap-2">
                    <FaStar className="w-4 h-4 sm:w-5 text-yellow-400" />
                    <span className="font-bold text-base sm:text-lg text-gray-800">
                      {displayRating.toFixed(1)}
                    </span>
                    <span className="text-gray-600 text-sm">
                      ({displayReviewsCount.toLocaleString()})
                    </span>
                  </div>
                ) : (
                  <span className="bg-gradient-to-r from-gray-100 to-gray-200 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 border border-gray-200">
                    ⭐ New on Flips
                  </span>
                )}
              </div>

              {/* Distance */}
              {vendor.distance &&
                vendor.distance !== "—" &&
                vendor.distance.toLowerCase() !== "n/a" && (
                  <p className="text-gray-700 text-sm font-medium mb-2 flex items-center gap-2">
                    {vendor.distance} away
                  </p>
                )}

              {/* Location */}
              {finalDisplayAddress && (
                <p className="text-gray-600 text-sm mb-3">
                  {finalDisplayAddress}
                </p>
              )}

              {/* Business Hours */}
              <div className="flex items-center gap-3">
                <span
                  className={`px-3 py-1.5 rounded-lg font-semibold text-xs sm:text-sm ${
                    businessInfo.status === "Open"
                      ? "bg-green-100 text-green-700 border border-green-200"
                      : businessInfo.status === "Closing Soon"
                      ? "bg-amber-100 text-amber-700 border border-amber-200"
                      : "bg-red-100 text-red-700 border border-red-200"
                  }`}
                >
                  {businessInfo.status}
                </span>
                <span className="text-gray-700 text-sm">
                  {businessInfo.status === "Open"
                    ? `Closes at ${businessInfo.timeDisplay}`
                    : businessInfo.timeDisplay}
                </span>
              </div>
            </div>

            {/* RIGHT SECTION - Price (aligned right on lg+, below on smaller) */}
             {minPrice && (
            <div className="flex flex-col items-start sm:items-end lg:mt-8">
              <p className="text-gray-600 text-xs sm:text-sm font-semibold uppercase tracking-wider mb-1">
                Starting from
              </p>
              <div className="relative">
                <div className="absolute inset-0 bg-orange-50 rounded-2xl blur-xl opacity-40 scale-125"></div>
                <p className="relative text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-orange-600 via-orange-500 to-[#fd5901]">
                  ₹{minPrice}
                </p>
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Services Chips */}
        {visibleServices.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t border-gray-200">
            {visibleServices.map((service, index) => (
              <p
                key={index}
                className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl text-gray-700 bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100 text-xs sm:text-sm font-semibold hover:shadow-md transition-shadow"
              >
                {service?.service_type_name}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterCard;
